import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ButtonGroup, Button, Tab, Card } from 'react-bootstrap'
import TransactionDetailGroup from './transaction-detail-group'
import DrawerAllocations from './allocations'
import DrawerMeters from './meters'
import ContractAccounts from './contract-accounts'
import CustomColumnsTab from './custom-columns-tab'
import ReceiptTab from './ReceiptTab'
import RemitAddresses from './remit-addresses'
import RemitInvoices from './remit-invoices'
import Features from 'transactions/drawer/features'

const TransactionDetailGroups = ({
  groupedDetails,
  allocations,
  meters,
  sectionName,
  transactionId,
  dashboardId,
  onPinField,
  pinnedFieldIds,
  canEdit = true,
  vendor,
  remitAddress,
  remitInvoices,
  receiptScan,
  testingData,
  auditReportId,
  initialFeatureTab
}) => {
  // Convert grouped details object to array for easier rendering
  const groups = Object.entries(groupedDetails || {})

  // Contracts use the grouped Accounts tab, not the raw allocations view.
  const showAllocations =
    allocations?.length > 0 && sectionName !== 'Budget' && sectionName !== 'Contracts'

  // Contracts carry no allocations of their own; surface the accounts tied to
  // them (via related POs/RQs/AP) in a dedicated tab for the CT section.
  const showContractAccounts = sectionName === 'Contracts'

  const showRemitAddresses =
    ['Vendors', 'Vendor Remittance'].includes(sectionName) && Array.isArray(vendor?.remit_addresses)
  const showRemittedTo = !!remitAddress
  const showRemitInvoices = Array.isArray(remitInvoices)

  // Determine if we should show meters tab (UB accounts → all meters on the account)
  const showMeters = meters?.length > 0

  // Determine if we should show custom columns tab
  const showCustomColumns = !!dashboardId

  // Determine if we should show receipt tab
  const showReceipt = !!receiptScan?.receipt_image_url
  const receiptTabLabel = receiptScan?.scan_type === 'invoice' ? 'Invoice Scan' : 'Receipt'

  // Determine if we should show testing tab
  const showTesting = !!testingData?.transaction_review

  // Sort groups to display "Calculated" last (before allocations)
  const sortedGroups = groups.sort(([groupNameA], [groupNameB]) => {
    if (groupNameA === 'Calculated') return 1
    if (groupNameB === 'Calculated') return -1
    return 0
  })

  // Set default active key: testing first when available, otherwise first group
  const defaultActiveKey = showTesting ? 'testing' : (sortedGroups.length > 0 ? sortedGroups[0][0] : (showAllocations ? 'allocations' : (showMeters ? 'meters' : 'custom-columns')))
  const [activeKey, setActiveKey] = useState(defaultActiveKey)

  useEffect(() => {
    if (showTesting) {
      setActiveKey('testing')
    }
  }, [transactionId, showTesting])

  // Don't render anything if there are no groups and no allocations and no custom columns
  if (groups.length === 0 && !showAllocations && !showMeters && !showCustomColumns && !showContractAccounts && !showRemitAddresses && !showRemittedTo && !showRemitInvoices) {
    return null
  }

  return (
    <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
      <ButtonGroup className="mb-3 shadow-sm rounded-start rounded-end">
        {showTesting && (
          <Button
            variant="light"
            onClick={() => setActiveKey('testing')}
            className={`py-1.5 px-3 ${
              activeKey === 'testing' ? 'tabs-active' : 'bg-white'
            }`}
          >
            Testing
          </Button>
        )}
        {sortedGroups.map(([groupName]) => (
          <Button
            key={groupName}
            variant="light"
            onClick={() => setActiveKey(groupName)}
            className={`py-1.5 px-3 ${
              activeKey === groupName ? 'tabs-active' : 'bg-white'
            }`}
          >
            {groupName}
          </Button>
        ))}
        {showAllocations && (
          <Button
            variant="light"
            onClick={() => setActiveKey('allocations')}
            className={`py-1.5 px-3 ${
              activeKey === 'allocations' ? 'tabs-active' : 'bg-white'
            }`}
          >
            Allocations
          </Button>
        )}
        {showRemittedTo && (
          <Button
            variant="light"
            onClick={() => setActiveKey('remitted-to')}
            className={`py-1.5 px-3 ${
              activeKey === 'remitted-to' ? 'tabs-active' : 'bg-white'
            }`}
          >
            Remitted To
          </Button>
        )}
        {showRemitInvoices && (
          <Button
            variant="light"
            onClick={() => setActiveKey('remit-invoices')}
            className={`py-1.5 px-3 ${
              activeKey === 'remit-invoices' ? 'tabs-active' : 'bg-white'
            }`}
          >
            Invoices ({remitInvoices.length})
          </Button>
        )}
        {showRemitAddresses && (
          <Button
            variant="light"
            onClick={() => setActiveKey('remit-addresses')}
            className={`py-1.5 px-3 ${
              activeKey === 'remit-addresses' ? 'tabs-active' : 'bg-white'
            }`}
          >
            Remit Addresses ({vendor.remit_addresses.length})
          </Button>
        )}
        {showMeters && (
          <Button
            variant="light"
            onClick={() => setActiveKey('meters')}
            className={`py-1.5 px-3 ${
              activeKey === 'meters' ? 'tabs-active' : 'bg-white'
            }`}
          >
            Meters
          </Button>
        )}
        {showContractAccounts && (
          <Button
            variant="light"
            onClick={() => setActiveKey('contract-accounts')}
            className={`py-1.5 px-3 ${
              activeKey === 'contract-accounts' ? 'tabs-active' : 'bg-white'
            }`}
          >
            Accounts
          </Button>
        )}
        {showCustomColumns && (
          <Button
            variant="light"
            onClick={() => setActiveKey('custom-columns')}
            className={`py-1.5 px-3 ${
              activeKey === 'custom-columns' ? 'tabs-active' : 'bg-white'
            }`}
          >
            Custom Columns
          </Button>
        )}
        {showReceipt && (
          <Button
            variant="light"
            onClick={() => setActiveKey('receipt')}
            className={`py-1.5 px-3 ${
              activeKey === 'receipt' ? 'tabs-active' : 'bg-white'
            }`}
          >
            {receiptTabLabel}
          </Button>
        )}
      </ButtonGroup>
      <Card>
        <Card.Body>
          <Tab.Content>
            {sortedGroups.map(([groupName, fields]) => (
              <Tab.Pane key={groupName} eventKey={groupName}>
                <TransactionDetailGroup
                  groupName={groupName}
                  fields={fields}
                  onPinField={onPinField}
                  pinnedFieldIds={pinnedFieldIds}
                  vendor={vendor}
                />
              </Tab.Pane>
            ))}
            {showAllocations && (
              <Tab.Pane eventKey="allocations">
                <DrawerAllocations allocations={allocations} sectionName={sectionName} />
              </Tab.Pane>
            )}
            {showRemittedTo && (
              <Tab.Pane eventKey="remitted-to">
                <RemitAddresses remitAddresses={[remitAddress]} />
              </Tab.Pane>
            )}
            {showRemitInvoices && (
              <Tab.Pane eventKey="remit-invoices">
                <RemitInvoices invoices={remitInvoices} />
              </Tab.Pane>
            )}
            {showRemitAddresses && (
              <Tab.Pane eventKey="remit-addresses">
                <RemitAddresses remitAddresses={vendor.remit_addresses} />
              </Tab.Pane>
            )}
            {showMeters && (
              <Tab.Pane eventKey="meters">
                <DrawerMeters meters={meters} />
              </Tab.Pane>
            )}
            {showContractAccounts && (
              <Tab.Pane eventKey="contract-accounts">
                <ContractAccounts
                  transactionId={transactionId}
                  sectionName={sectionName}
                  active={activeKey === 'contract-accounts'}
                />
              </Tab.Pane>
            )}
            {showCustomColumns && (
              <Tab.Pane eventKey="custom-columns">
                <CustomColumnsTab
                  transactionId={transactionId}
                  dashboardId={dashboardId}
                  onPinField={onPinField}
                  pinnedFieldIds={pinnedFieldIds}
                  canEdit={canEdit}
                />
              </Tab.Pane>
            )}
            {showReceipt && (
              <Tab.Pane eventKey="receipt">
                <ReceiptTab receiptScan={receiptScan} />
              </Tab.Pane>
            )}
            {showTesting && (
              <Tab.Pane eventKey="testing">
                <Features
                  transactionReview={testingData.transaction_review}
                  features={testingData.features}
                  auditReportId={auditReportId}
                  initialFeatureTab={initialFeatureTab}
                  canEdit={canEdit}
                />
              </Tab.Pane>
            )}
          </Tab.Content>
        </Card.Body>
      </Card>
    </Tab.Container>
  )
}

TransactionDetailGroups.propTypes = {
  groupedDetails: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        display_name: PropTypes.string.isRequired,
        value: PropTypes.any,
        display_as: PropTypes.string.isRequired,
        database_column_name: PropTypes.string.isRequired
      })
    )
  ),
  allocations: PropTypes.array,
  meters: PropTypes.array,
  sectionName: PropTypes.string,
  transactionId: PropTypes.string,
  dashboardId: PropTypes.string,
  onPinField: PropTypes.func,
  pinnedFieldIds: PropTypes.instanceOf(Set),
  canEdit: PropTypes.bool,
  vendor: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    external_id: PropTypes.string,
    remit_addresses: PropTypes.array
   }),
  remitAddress: PropTypes.object,
  remitInvoices: PropTypes.array,
  receiptScan: PropTypes.object,
  testingData: PropTypes.shape({
    transaction_review: PropTypes.object,
    features: PropTypes.array
  })
}

export default TransactionDetailGroups
